import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

export type IntegrationTab =
  | "history"
  | "client-history"
  | "files"
  | "settings";

const TAB_PARAM = "tab";
const RUN_PARAM = "run";

export interface IntegrationDetailsUrlState {
  tab: IntegrationTab;
  /** Selected run (`?run=`), null when none or unparsable. */
  runId: number | null;
  /** Switches the tab and clears the selected run. */
  setTab: (tab: IntegrationTab) => void;
  setRunId: (runId: number | null) => void;
  /** Cross-link: open a run inside another tab in one URL write. */
  openRunIn: (tab: IntegrationTab, runId: number) => void;
}

function pickTab(
  allowed: readonly IntegrationTab[],
  raw: string | null | undefined,
): IntegrationTab | null {
  return raw && (allowed as readonly string[]).includes(raw)
    ? (raw as IntegrationTab)
    : null;
}

function parseRunId(raw: string | null): number | null {
  if (!raw) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * The integration details page's addressable state: `?tab=` and `?run=`.
 *
 * A tab the current user may not open is coerced to `fallback` (a client user
 * following `?tab=settings` lands on their history) - a pure derivation, no
 * snap-back effect. `location.state.initialTab` (deep links from the wizard
 * and Client & User Setup) is honoured as a fallback.
 *
 * Every write REPLACES the history entry and carries `location.state` along,
 * so browser Back leaves the page exactly as before and `fromUserManagement`
 * survives for the page's own Back button. Reads never write.
 */
export function useIntegrationDetailsUrlState(
  allowed: readonly IntegrationTab[],
  fallback: IntegrationTab,
): IntegrationDetailsUrlState {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const legacyTab = (location.state as { initialTab?: string } | null)
    ?.initialTab;

  const tab =
    pickTab(allowed, params.get(TAB_PARAM)) ??
    pickTab(allowed, legacyTab) ??
    fallback;
  const runId = parseRunId(params.get(RUN_PARAM));

  const write = useCallback(
    (patch: { tab?: IntegrationTab; run?: number | null }) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (patch.tab !== undefined) next.set(TAB_PARAM, patch.tab);
          if (patch.run === null) next.delete(RUN_PARAM);
          else if (patch.run !== undefined)
            next.set(RUN_PARAM, String(patch.run));
          return next;
        },
        { replace: true, state: location.state },
      );
    },
    [setParams, location.state],
  );

  return useMemo(
    () => ({
      tab,
      runId,
      setTab: (next: IntegrationTab) => write({ tab: next, run: null }),
      setRunId: (id: number | null) => write({ run: id }),
      openRunIn: (next: IntegrationTab, id: number) =>
        write({ tab: next, run: id }),
    }),
    [tab, runId, write],
  );
}
